import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { PiEnvelopeSimple, PiIdentificationBadge } from 'react-icons/pi';
import { useParams } from 'react-router-dom';
import { Button, Input, Switch } from 'rizzui';
import { toast } from 'sonner';
import useFetch from '../../../../hooks/useFetch';
import useHandleErrors from '../../../../hooks/useHandleErrors';
import FormGroup from '../../../../layout/shared/form-group';
import { CallsService } from '../../../../services/calls/callsService';
import { CallOcmExtendataItem, CallsApiResponse } from '../../../../type/entities/calls-type';
import CallProfileLayout from '../CallProfileLayout';
import { PiCalendar } from 'react-icons/pi';
import { PiClock } from 'react-icons/pi';
import { PiNetwork } from 'react-icons/pi';
import { usePrivilege } from '../../../../components/priviledge/PriviledgeProvider';

interface CallInfoProps { }

const CallInfo: React.FC<CallInfoProps> = () => {

    const { id = '' } = useParams<{ id: string }>();
    const service = new CallsService();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const canEditCall = userCan('edit_calls', 'calls') || userCan('admin_calls', 'calls');
    const [countsAsFailedVerificationCall, setCountsAsFailedVerificationCall] = useState(false);
    const [isSavingCountFlag, setIsSavingCountFlag] = useState(false);

    const [data, loading] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getCall(id);
        return response.getResponseData() as CallsApiResponse;
    }, [id]));

    useEffect(() => {
        setCountsAsFailedVerificationCall(Boolean(data?.countsAsFailedVerificationCall));
    }, [data?.countsAsFailedVerificationCall]);

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES FORMATEA LA DURACIÓN DE UNA LLAMADA EN HUMAN READABLE
     * @EN FORMAT THE CALL DURATION IN HUMAN READABLE
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const formatDurationHuman = (seconds: number): string => {
        if (!seconds && seconds !== 0) return 'N/A';

        const totalSeconds = Math.round(seconds);

        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;

        let parts = [];
        if (h > 0) parts.push(`${h} h`);
        if (m > 0) parts.push(`${m} min`);
        if (s > 0 || parts.length === 0) parts.push(`${s} s`);

        return parts.join(' ');
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    const extendataItems: CallOcmExtendataItem[] = Array.isArray(data?.ocmExtendata) ? data.ocmExtendata : [];

    const getExtendataValue = (item: CallOcmExtendataItem): string => {
        const value = typeof item?.value === 'string' ? item.value.trim() : item?.value;

        return value ? String(value) : 'Sin datos';
    };

    const saveCountFlag = async () => {
        if (!id || !data) return;

        setIsSavingCountFlag(true);

        try {
            const response = await service.editCall({
                callId: id,
                userId: data?.user?.id || data?.userId || null,
                comment: data?.comment || '',
                countsAsFailedVerificationCall,
            });

            const responseData = await response.getResponseData();
            if (!responseData?.success) {
                handleErrors(responseData);
                return;
            }

            toast.success('Marcado de incidencia automática actualizado');
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSavingCountFlag(false);
        }
    };

    return (
        <CallProfileLayout isLoading={loading}>
            <div className="@container">
                <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200">
                    <FormGroup
                        title="Información General"
                        description="Datos principales"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Input
                            readOnly
                            prefix={<PiCalendar className="h-6 w-6 text-gray-500" />}
                            label="Fecha de llamada"
                            placeholder="Fecha de llamada"
                            value={data?.callDate.date ?? ""}
                        />
                        <Input
                            readOnly
                            prefix={<PiClock className="h-6 w-6 text-gray-500" />}
                            label="Duración"
                            placeholder="Duración"
                            value={formatDurationHuman(data?.duration)}
                        />
                        <Input
                            readOnly
                            label="ID OCM"
                            placeholder="ID OCM"
                            value={data?.actionId || ""}
                        />
                        <Input
                            readOnly
                            label="NIF OCM"
                            placeholder="NIF OCM"
                            value={data?.nif || ""}
                        />
                        <Input
                            readOnly
                            label="Resultado"
                            placeholder="Resultado"
                            value={data?.classification?.name || data?.status || ""}
                        />
                        <Input
                            readOnly
                            label="Comentario"
                            placeholder="Comentario"
                            value={data?.comment || ""}
                        />
                        <div className="md:col-span-2 flex flex-col gap-3 rounded-md border border-gray-200 px-4 py-3">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <p className="text-sm font-medium text-gray-900">Cuenta para incidencia automática</p>
                                    <p className="text-xs text-gray-500">Define si esta llamada concreta suma para crear `Contrato sin verificar`.</p>
                                </div>
                                <Switch
                                    checked={countsAsFailedVerificationCall}
                                    onChange={() => canEditCall && setCountsAsFailedVerificationCall((current) => !current)}
                                    disabled={!canEditCall || isSavingCountFlag}
                                />
                            </div>
                            {canEditCall && (
                                <div className="flex justify-end">
                                    <Button size="sm" onClick={saveCountFlag} disabled={isSavingCountFlag}>
                                        {isSavingCountFlag ? 'Guardando...' : 'Guardar marcado'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </FormGroup>
                    <FormGroup
                        title="Agente"
                        description="Datos relacionados con el agente"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Input
                            readOnly
                            label="Agente"
                            placeholder="Agente"
                            value={data?.user?.name + " " + data?.user?.lastName || ""}
                        />
                        <Input
                            readOnly
                            prefix={<PiEnvelopeSimple className="h-6 w-6 text-gray-500" />}
                            label="Email de agente"
                            placeholder="Email de agente"
                            value={data?.user?.email || ""}
                        />
                        <Input
                            readOnly
                            prefix={<PiNetwork className="h-6 w-6 text-gray-500" />}
                            label="Red comercial"
                            placeholder="Red comercial"
                            value={data?.commercialNetwork?.name || ""}
                        />
                        <Input
                            readOnly
                            label="Departamento"
                            placeholder="Departamento"
                            value={data?.user?.department?.name || ""}
                        />
                    </FormGroup>
                    <FormGroup
                        title="Cliente"
                        description="Datos relacionados con el cliente"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Input
                            readOnly
                            label="Razón Social"
                            placeholder="Cliente"
                            value={data?.client?.companyName || ""}
                        />
                        <Input
                            readOnly
                            prefix={<PiEnvelopeSimple className="h-6 w-6 text-gray-500" />}
                            label="Email de agente"
                            placeholder="Email de agente"
                            value={data?.user?.email || ""}
                        />
                        <Input
                            readOnly
                            prefix={<PiIdentificationBadge className="h-6 w-6 text-gray-500" />}
                            label="CIF/NIF"
                            placeholder="CIF/NIF"
                            value={data?.client?.cifNif || ""}
                        />
                    </FormGroup>
                    <FormGroup
                        title="Datos OCM"
                        description="Resultado recibido desde OCM"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Input
                            readOnly
                            label="Tipificación final"
                            placeholder="Tipificación final"
                            value={data?.ocmEndText || "Sin datos"}
                        />
                        <Input
                            readOnly
                            label="Grupo de tipificación final"
                            placeholder="Grupo de tipificación final"
                            value={data?.ocmEndGroup || "Sin datos"}
                        />
                    </FormGroup>
                    <FormGroup
                        title="Información de OCM"
                        description="Campos adicionales recibidos desde OCM"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        {extendataItems.length > 0 ? (
                            extendataItems.map((item, index) => (
                                <Input
                                    key={`${item.name || 'ocm-field'}-${index}`}
                                    readOnly
                                    label={item.name || `Campo OCM ${index + 1}`}
                                    placeholder={item.name || `Campo OCM ${index + 1}`}
                                    value={getExtendataValue(item)}
                                />
                            ))
                        ) : (
                            <Input
                                readOnly
                                label="Extendata OCM"
                                placeholder="Extendata OCM"
                                value="Sin datos"
                            />
                        )}
                    </FormGroup>
                </div>
            </div>
        </CallProfileLayout>
    );
};

export default CallInfo;
